import { useCallback, useRef, useState } from 'react';

interface AudioFile {
  filename: string;
  duration?: number; // in milliseconds
}

export const useKAudio = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);
  const activeAudioElements = useRef<Set<HTMLAudioElement>>(new Set());
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  const enableAudio = useCallback(() => {
    setUserHasInteracted(true);
  }, []);

  // Enhanced detection: Check multiple sources for any playing audio
  const isAnyAudioPlaying = useCallback(() => {
    // Layer 1: Check our playing flag
    if (isPlayingRef.current) {
      return true;
    }

    // Layer 2: Check our tracked audio element
    if (audioRef.current && !audioRef.current.paused) {
      return true;
    }

    // Layer 3: Check all our created audio elements
    const activeAudioArray = Array.from(activeAudioElements.current);
    for (const audio of activeAudioArray) {
      if (!audio.paused) {
        return true;
      }
    }

    // Layer 4: Global DOM audio detection (nuclear option)
    const allAudio = Array.from(document.querySelectorAll('audio'));
    for (const audio of allAudio) {
      if (!audio.paused) {
        return true;
      }
    }

    return false;
  }, []);

  // Force stop all audio - aggressive cleanup
  const forceStopAllAudio = useCallback(() => {
    // Stop our tracked audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Stop all our created audio elements
    const activeAudioArray = Array.from(activeAudioElements.current);
    for (const audio of activeAudioArray) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch {
        // Ignore errors when stopping audio
      }
    }

    // Nuclear option: stop ALL audio in DOM
    const allAudio = Array.from(document.querySelectorAll('audio'));
    for (const audio of allAudio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch {
        // Ignore errors when stopping audio
      }
    }

    // Reset all flags
    isPlayingRef.current = false;
    activeAudioElements.current.clear();
  }, []);

  const playAudio = useCallback(async (audioFile: AudioFile): Promise<void> => {
    // ENHANCED RULE: If ANY audio is detected playing, don't start new audio
    if (isAnyAudioPlaying()) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        // Create new audio element
        const audio = new Audio(`/assets/audio/${audioFile.filename}`);
        audioRef.current = audio;
        activeAudioElements.current.add(audio);
        isPlayingRef.current = true;

        // Set up event handlers
        audio.onended = () => {
          isPlayingRef.current = false;
          activeAudioElements.current.delete(audio);
          resolve();
        };

        audio.onerror = (error) => {
          isPlayingRef.current = false;
          activeAudioElements.current.delete(audio);
          console.error(`Failed to load audio: ${audioFile.filename}`, error);
          reject(new Error(`Audio file not found: ${audioFile.filename}`));
        };

        audio.oncanplaythrough = () => {
          // Try to play immediately
          audio.play()
            .then(() => {
              // Audio started successfully
            })
            .catch((error) => {
              // Handle autoplay restrictions
              isPlayingRef.current = false;
              if (error.name === 'NotAllowedError') {
                activeAudioElements.current.delete(audio);
                resolve(); // Don't treat as error
              } else {
                console.error(`Audio play error: ${audioFile.filename}`, error);
                activeAudioElements.current.delete(audio);
                reject(error);
              }
            });
        };

        // Load the audio
        audio.load();
      } catch (error) {
        isPlayingRef.current = false;
        if (audioRef.current) {
          activeAudioElements.current.delete(audioRef.current);
        }
        reject(error);
      }
    });
  }, [userHasInteracted, isAnyAudioPlaying, forceStopAllAudio]);

  const stopAudio = useCallback(() => {
    forceStopAllAudio();
  }, [forceStopAllAudio]);

  const isPlaying = useCallback(() => {
    return isAnyAudioPlaying();
  }, [isAnyAudioPlaying]);

  return {
    playAudio,
    isPlaying,
    enableAudio,
    userHasInteracted,
    isAnyAudioPlaying,
    forceStopAllAudio
  };
};

// Audio file definitions for K SelectionScreen
export const K_SELECTION_SCREEN_AUDIO = {
  welcome: { filename: 'k_welcome.mp3', duration: 2000 },
  instructions: { filename: 'k_instructions.mp3', duration: 2500 },
  encouragement: { filename: 'k_encouragement.mp3', duration: 2000 },
  buttonHover: { filename: 'ui_button_hover_01.mp3', duration: 500 }
} as const;

// Audio file definitions for K QuestionScreen - Number Naming, Subitizing & Counting
export const K_QUESTION_SCREEN_AUDIO = {
  // Number instruction files (0-20 for K level)
  // Uses PreK audio files for 0-9, K-specific files for 10-20
  tapNumber: {
    0: { filename: 'preKAssessment/numberNaming/question_tap_number_0.mp3', duration: 1000 },
    1: { filename: 'preKAssessment/numberNaming/question_tap_number_1.mp3', duration: 1000 },
    2: { filename: 'preKAssessment/numberNaming/question_tap_number_2.mp3', duration: 1000 },
    3: { filename: 'preKAssessment/numberNaming/question_tap_number_3.mp3', duration: 1000 },
    4: { filename: 'preKAssessment/numberNaming/question_tap_number_4.mp3', duration: 1000 },
    5: { filename: 'preKAssessment/numberNaming/question_tap_number_5.mp3', duration: 1000 },
    6: { filename: 'preKAssessment/numberNaming/question_tap_number_6.mp3', duration: 1000 },
    7: { filename: 'preKAssessment/numberNaming/question_tap_number_7.mp3', duration: 1000 },
    8: { filename: 'preKAssessment/numberNaming/question_tap_number_8.mp3', duration: 1000 },
    9: { filename: 'preKAssessment/numberNaming/question_tap_number_9.mp3', duration: 1000 },
    10: { filename: 'kAssessment/numberNaming/k_tap_number_10.mp3', duration: 1000 },
    11: { filename: 'kAssessment/numberNaming/k_tap_number_11.mp3', duration: 1000 },
    12: { filename: 'kAssessment/numberNaming/k_tap_number_12.mp3', duration: 1000 },
    13: { filename: 'kAssessment/numberNaming/k_tap_number_13.mp3', duration: 1000 },
    14: { filename: 'kAssessment/numberNaming/k_tap_number_14.mp3', duration: 1000 },
    15: { filename: 'kAssessment/numberNaming/k_tap_number_15.mp3', duration: 1000 },
    16: { filename: 'kAssessment/numberNaming/k_tap_number_16.mp3', duration: 1000 },
    17: { filename: 'kAssessment/numberNaming/k_tap_number_17.mp3', duration: 1000 },
    18: { filename: 'kAssessment/numberNaming/k_tap_number_18.mp3', duration: 1000 },
    19: { filename: 'kAssessment/numberNaming/k_tap_number_19.mp3', duration: 1000 },
    20: { filename: 'kAssessment/numberNaming/k_tap_number_20.mp3', duration: 1000 }
  },
  // Subitizing instructions (K level - 1-6)
  // Uses PreK instruction audio for compatibility
  subitizing: {
    instruction: { filename: 'subitizing_instruction.mp3', duration: 1500 },
    assessmentIntro: { filename: 'preKAssessment/subitizing/subitizing_intro.mp3', duration: 5000 },
    getReady: { filename: 'preKAssessment/subitizing/subitizing_get_ready.mp3', duration: 1000 },
    warmupStart: { filename: 'kAssessment/subitizing/k_subitizing_warmup_start.mp3', duration: 3000 },
    warmupReady: { filename: 'kAssessment/subitizing/k_subitizing_warmup_ready.mp3', duration: 2000 },
    timeoutPrompt: { filename: 'kAssessment/subitizing/k_subitizing_timeout_prompt.mp3', duration: 2000 },
    timeoutEncourage: { filename: 'kAssessment/subitizing/k_subitizing_timeout_encourage.mp3', duration: 2500 },
    // Demo phase - think-aloud modeling before assessment (shared with PreK)
    demoIntro: { filename: 'assessment/subitizing/demo_intro.mp3', duration: 6000 },
    demoISaw3: { filename: 'assessment/subitizing/demo_i_saw_3.mp3', duration: 4000 },
    demoYourTurn: { filename: 'assessment/subitizing/demo_your_turn.mp3', duration: 2000 }
  },
  // Counting instructions (K level - 1-10)
  // Uses PreK instruction audio for compatibility
  counting: {
    instruction: { filename: 'counting_instruction.mp3', duration: 2000 }
  },
  // Comparing instructions (K level)
  comparing: {
    instruction: { filename: 'kAssessment/comparing/k_comparing_instruction.mp3', duration: 1500 },
    // I Do Practice - intro and results
    iDoIntro: { filename: 'kPractice/comparison/iDo/k_comparison_ido_intro.mp3', duration: 1500 },
    iDoResult: {
      1: { filename: 'kPractice/comparison/iDo/k_comparison_result_1.mp3', duration: 1500 },
      2: { filename: 'kPractice/comparison/iDo/k_comparison_result_2.mp3', duration: 1500 },
      3: { filename: 'kPractice/comparison/iDo/k_comparison_result_3.mp3', duration: 1500 },
      4: { filename: 'kPractice/comparison/iDo/k_comparison_result_4.mp3', duration: 1500 },
      5: { filename: 'kPractice/comparison/iDo/k_comparison_result_5.mp3', duration: 1500 },
      6: { filename: 'kPractice/comparison/iDo/k_comparison_result_6.mp3', duration: 1500 },
      7: { filename: 'kPractice/comparison/iDo/k_comparison_result_7.mp3', duration: 1500 },
      8: { filename: 'kPractice/comparison/iDo/k_comparison_result_8.mp3', duration: 1500 },
      9: { filename: 'kPractice/comparison/iDo/k_comparison_result_9.mp3', duration: 1500 },
      10: { filename: 'kPractice/comparison/iDo/k_comparison_result_10.mp3', duration: 1500 }
    },
    // We Do Practice - intro (reuses iDoResult for results)
    weDoIntro: { filename: 'kPractice/comparison/weDo/k_comparison_wedo_intro.mp3', duration: 2000 }
  },
  // Part Whole instructions (K level)
  partWhole: {
    instruction: { filename: 'kAssessment/partWhole/k_partwhole_instruction.mp3', duration: 1500 }
  },
  // Addition Within 5 instructions (K level)
  additionWithin5: {
    instruction: { filename: 'kAssessment/additionWithin5/k_addition_instruction.mp3', duration: 1500 }
  },
  // Warmup instructions
  warmup: {
    practiceInstruction: { filename: 'k_warmup_practice_instruction.mp3', duration: 2000 },
    goodJob: { filename: 'k_warmup_good_job.mp3', duration: 1500 },
    readyForTest: { filename: 'k_warmup_ready_for_test.mp3', duration: 2000 },
    transition: { filename: 'preKAssessment/warmup_transition.mp3', duration: 5000 }
  },
  // Positive feedback (K-specific variety for engagement)
  feedback: {
    correct1: { filename: 'k_feedback_correct_1.mp3', duration: 1000 },
    correct2: { filename: 'k_feedback_correct_2.mp3', duration: 1000 },
    correct3: { filename: 'k_feedback_correct_3.mp3', duration: 1000 },
    correct4: { filename: 'k_feedback_correct_4.mp3', duration: 1000 },
    correct5: { filename: 'k_feedback_correct_5.mp3', duration: 1000 }
  },
  // Timeout recovery prompts
  timeout: {
    gentlePrompt: { filename: 'k_timeout_gentle_prompt.mp3', duration: 2000 },
    encouragement: { filename: 'k_timeout_encouragement.mp3', duration: 2000 }
  },
  // Test completion
  completion: { filename: 'k_test_completion.mp3', duration: 2000 },
  // Practice phase transitions (reusing PreK audio files)
  transitions: {
    accuracyPass: { filename: 'preKPractice/numberNaming/transitions/fluency15_pass.mp3', duration: 2000 },
    fluency15Pass: { filename: 'preKPractice/numberNaming/transitions/fluency15_pass.mp3', duration: 2000 },
    fluency15Fail: { filename: 'preKPractice/numberNaming/transitions/fluency15_fail.mp3', duration: 2000 }
  }
} as const;

// Alias for consistent imports across PreK and K components
export const QUESTION_SCREEN_AUDIO = K_QUESTION_SCREEN_AUDIO;

// Programmatic sound generation for immediate feedback (same as PreK)
const createProgrammaticSound = (type: 'chime' | 'boop'): Promise<void> => {
  return new Promise((resolve) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const gainNode = audioContext.createGain();
      gainNode.connect(audioContext.destination);

      if (type === 'chime') {
        // Create pleasant ascending chime (C-E-G chord)
        const frequencies = [261.63, 329.63, 392.00]; // C4, E4, G4
        const duration = 0.4; // 400ms

        frequencies.forEach((freq, index) => {
          const oscillator = audioContext.createOscillator();
          const noteGain = audioContext.createGain();

          oscillator.connect(noteGain);
          noteGain.connect(gainNode);

          oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
          oscillator.type = 'sine';

          // Gentle attack and decay
          noteGain.gain.setValueAtTime(0, audioContext.currentTime);
          noteGain.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
          noteGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

          oscillator.start(audioContext.currentTime + index * 0.1);
          oscillator.stop(audioContext.currentTime + duration);
        });

        setTimeout(resolve, duration * 1000);
      } else {
        // Create short boop sound (lower frequency buzz)
        const oscillator = audioContext.createOscillator();
        oscillator.connect(gainNode);

        oscillator.frequency.setValueAtTime(150, audioContext.currentTime); // Low frequency
        oscillator.type = 'square'; // Harsher sound for error

        // Quick attack and decay
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.25);

        setTimeout(resolve, 250);
      }
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
      resolve(); // Don't block if audio fails
    }
  });
};

// Specialized hook for K TTS with timer coordination
export const useKTTS = () => {
  const { playAudio, isPlaying, isAnyAudioPlaying, forceStopAllAudio } = useKAudio();
  const [isTTSPlaying, setIsTTSPlaying] = useState(false);
  const [ttsCompletedRef, setTTSCompletedRef] = useState(false);

  // Play TTS and return a promise that resolves when audio completes
  const playTTSWithCallback = useCallback(async (audioFile: AudioFile, onComplete?: () => void): Promise<void> => {
    setIsTTSPlaying(true);
    setTTSCompletedRef(false);

    try {
      await playAudio(audioFile);
      setTTSCompletedRef(true);
      setIsTTSPlaying(false);

      // Call completion callback after TTS finishes
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('K TTS playback failed:', error);
      setTTSCompletedRef(true);
      setIsTTSPlaying(false);

      // Still call completion callback even if audio fails
      if (onComplete) {
        onComplete();
      }
    }
  }, [playAudio]);

  // Play random feedback audio from available K options
  const playRandomFeedback = useCallback(async (): Promise<void> => {
    // Extra safety check before playing feedback
    if (isAnyAudioPlaying()) {
      return Promise.resolve();
    }

    const feedbackOptions = [
      K_QUESTION_SCREEN_AUDIO.feedback.correct1,
      K_QUESTION_SCREEN_AUDIO.feedback.correct2,
      K_QUESTION_SCREEN_AUDIO.feedback.correct3,
      K_QUESTION_SCREEN_AUDIO.feedback.correct4,
      K_QUESTION_SCREEN_AUDIO.feedback.correct5
    ];

    const randomFeedback = feedbackOptions[Math.floor(Math.random() * feedbackOptions.length)];
    return await playAudio(randomFeedback);
  }, [playAudio, isAnyAudioPlaying]);

  // Stop any current TTS - AGGRESSIVE CLEANUP
  const stopTTS = useCallback(() => {
    forceStopAllAudio();
    setIsTTSPlaying(false);
    setTTSCompletedRef(true);
  }, [forceStopAllAudio]);

  // Play immediate feedback sounds (always play regardless of speechEnabled)
  const playChime = useCallback(async (): Promise<void> => {
    return createProgrammaticSound('chime');
  }, []);

  const playBoop = useCallback(async (): Promise<void> => {
    return createProgrammaticSound('boop');
  }, []);

  return {
    playTTSWithCallback,
    playRandomFeedback,
    stopTTS,
    isTTSPlaying,
    ttsCompleted: ttsCompletedRef,
    playChime,
    playBoop,
    // Expose original audio functions
    playAudio,
    isPlaying
  };
};